import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  useGetEmployeeDiagnosticAssignmentsQuery,
  useStartDiagnosticSubmissionMutation,
  useGetDiagnosticSubmissionByIdQuery,
  useLazyGetMyAggregatedFeedbackQuery,
  useGetMyDiagnosticRequestQuery,
  useCreateDiagnosticRequestMutation,
  useUpdateMyDiagnosticRequestMutation,
  useGetMyTeamMembersQuery,
} from "../../services/api";
import "./Diagnostic.css";

// View components
import FormListView from "./views/FormListView";
import FormFillView from "./views/FormFillView";
import ResultsView from "./views/ResultsView";
import CompletedView from "./views/CompletedView";
import DiagnosticRequestView from "./views/DiagnosticRequestView";

/**
 * Main Diagnostic Component - Orchestrator
 * Manages view state and data flow between child components
 */
export default function Diagnostic() {
  const [view, setView] = useState("list"); // 'list' | 'form' | 'results' | 'completed'
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [activeSubmission, setActiveSubmission] = useState(null);

  const user = useSelector((state) => state.auth.user);
  const companyId = user?.company?._id || user?.company;
  const userId = user?.id || user?._id;

  // Fetch assignments for current employee
  // refetchOnMountOrArgChange ensures fresh data when navigating to this page
  const {
    data: assignments = [],
    isLoading,
    refetch,
  } = useGetEmployeeDiagnosticAssignmentsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Fetch current diagnostic request
  const {
    data: diagnosticRequest,
    isLoading: isLoadingRequest,
    refetch: refetchRequest,
  } = useGetMyDiagnosticRequestQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Fetch company employees for diagnoser selection
  const { data: companyUsersData, isLoading: isLoadingUsers } = useGetMyTeamMembersQuery(
    { companyId },
    { skip: !companyId }
  );
  const companyUsers = companyUsersData?.members || [];

  // Request mutations
  const [createRequest, { isLoading: isCreating }] = useCreateDiagnosticRequestMutation();
  const [updateRequest, { isLoading: isUpdating }] = useUpdateMyDiagnosticRequestMutation();

  // Start submission mutation
  const [startSubmission] = useStartDiagnosticSubmissionMutation();

  // Fetch submission when we have an active assignment with submission
  const { data: submissionData, refetch: refetchSubmission } =
    useGetDiagnosticSubmissionByIdQuery(activeSubmission?._id, {
      skip: !activeSubmission?._id,
    });

  // Lazy fetch aggregated feedback - triggered manually to avoid race conditions
  // The second parameter (true) in fetchAggregatedFeedback bypasses cache for fresh data
  const [fetchAggregatedFeedback, { data: aggregatedFeedback, isLoading: isLoadingAggregated }] =
    useLazyGetMyAggregatedFeedbackQuery();

  // Determine if we should show the request flow
  const hasApprovedRequest = diagnosticRequest?.requestStatus === "approved";
  const hasActiveAssignments = assignments && assignments.length > 0;
  const showRequestFlow = !hasActiveAssignments && !hasApprovedRequest;

  // Check if user has results to view
  const hasResultsToView = assignments?.some((a) => {
    const isTarget = a.target?._id === userId || a.target === userId;
    const isSubmitted = a.submission?.status === "submitted";
    return isTarget && isSubmitted;
  });

  // Handle creating a diagnostic request
  const handleCreateRequest = async (data) => {
    try {
      await createRequest(data).unwrap();
      refetchRequest();
    } catch (error) {
      console.error("Failed to create request:", error);
      alert(error.data?.message || "Failed to submit request");
    }
  };

  // Handle updating a diagnostic request
  const handleUpdateRequest = async (data) => {
    try {
      await updateRequest(data).unwrap();
      refetchRequest();
    } catch (error) {
      console.error("Failed to update request:", error);
      alert(error.data?.message || "Failed to update request");
    }
  };

  // Handle selecting an assignment
  const handleSelectAssignment = async (assignment) => {
    setActiveAssignment(assignment);

    // If already completed, show completed view
    if (assignment.submission?.status === "submitted") {
      setActiveSubmission(assignment.submission);
      setView("completed");
      return;
    }

    // If already started, continue
    if (assignment.submission) {
      setActiveSubmission(assignment.submission);
      setView("form");
      return;
    }

    // Start new submission
    try {
      const result = await startSubmission({
        assignmentId: assignment._id,
      }).unwrap();
      setActiveSubmission(result.submission);
      setView("form");
      refetch();
    } catch (error) {
      console.error("Failed to start submission:", error);
      alert("Failed to start form. Please try again.");
    }
  };

  // Handle going back to list
  const handleBackToList = () => {
    setView("list");
    setActiveAssignment(null);
    setActiveSubmission(null);
    refetch();
  };

  // Handle form completion
  const handleFormComplete = () => {
    setView("completed");
    refetchSubmission();
    refetch();
  };

  // Handle viewing my results
  const handleViewMyResults = async () => {
    const relevantAssignment = assignments?.find((a) => {
      const isTarget = a.target?._id === userId || a.target === userId;
      const isSubmitted = a.submission?.status === "submitted";
      return isTarget && isSubmitted;
    });
    if (relevantAssignment) {
      setActiveAssignment(relevantAssignment);
      setActiveSubmission(relevantAssignment.submission);
      // Set view first to show loading state
      setView("results");
      // Force fresh fetch (bypasses cache) to ensure we get latest data
      await fetchAggregatedFeedback({ templateId: relevantAssignment.template?._id }, true);
    }
  };

  // Page title based on view
  const pageTitle =
    view === "results"
      ? "Diagnostic Results"
      : view === "form"
      ? "Complete Diagnostic"
      : "Diagnostics";

  const backLabel = view === "list" ? "← Back Home" : "← Back to Diagnostics";

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const combinedLoading = isLoading || isLoadingRequest;

  return (
    <div className="diagnostic-container">
      {/* Breadcrumb */}
      <button
        className="welcome"
        onClick={view !== "list" ? handleBackToList : undefined}
      >
        {backLabel}
      </button>

      {/* Title */}
      <h1 className="username">{pageTitle}</h1>

      {/* Loading State */}
      {combinedLoading && view === "list" && (
        <div className="diagnostic-card">
          <div className="loading-state">Loading your diagnostic forms...</div>
        </div>
      )}

      {/* Request Flow View - shown when no assignments exist */}
      {view === "list" && !combinedLoading && showRequestFlow && (
        <DiagnosticRequestView
          request={diagnosticRequest}
          coworkers={companyUsers}
          isLoadingCoworkers={isLoadingUsers}
          currentUserId={userId}
          onCreateRequest={handleCreateRequest}
          onUpdateRequest={handleUpdateRequest}
          isCreating={isCreating}
          isUpdating={isUpdating}
        />
      )}

      {/* My Results Section - shown when user has completed evaluations */}
      {view === "list" && !combinedLoading && !showRequestFlow && hasResultsToView && (
        <div className="my-results-section">
          <div className="my-results-card">
            <div className="my-results-content">
              <h3 className="my-results-title">My Diagnostic Results</h3>
              <p className="my-results-desc">
                View your aggregated feedback from self and colleague evaluations
              </p>
            </div>
            <button className="my-results-btn" onClick={handleViewMyResults}>
              View My Results →
            </button>
          </div>
        </div>
      )}

      {/* List View - shown when assignments exist */}
      {view === "list" && !combinedLoading && !showRequestFlow && (
        <FormListView
          assignments={assignments}
          onSelect={handleSelectAssignment}
        />
      )}

      {/* Form Fill View */}
      {view === "form" && activeAssignment && activeSubmission && (
        <FormFillView
          assignment={activeAssignment}
          submission={submissionData || activeSubmission}
          onBack={handleBackToList}
          onComplete={handleFormComplete}
        />
      )}

      {/* Results View */}
      {view === "results" && activeSubmission && (
        <ResultsView
          submission={submissionData || activeSubmission}
          assignment={activeAssignment}
          aggregatedData={aggregatedFeedback}
          isLoadingAggregated={isLoadingAggregated}
          onBack={handleBackToList}
        />
      )}

      {/* Completed View */}
      {view === "completed" && activeSubmission && activeAssignment && (
        <CompletedView
          submission={submissionData || activeSubmission}
          assignment={activeAssignment}
          onBack={handleBackToList}
        />
      )}
    </div>
  );
}
